import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Ctx = createContext(null);

export function RealtimeProvider({ children }) {
  const qc = useQueryClient();
  const subs = useRef(new Set());

  useEffect(() => {
    let ws;
    let timer;
    let ping;
    let stopped = false;
    let tries = 0;
    const connect = async () => {
      if (tries > 0) {
        try {
          await api.post("/auth/refresh");
        } catch {
          /* session may be gone */
        }
      }
      if (stopped) return;
      ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`);
      ws.onopen = () => {
        if (tries > 0) subs.current.forEach((fn) => fn({ type: "reconnected" }));
        tries = 0;
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      };
      ws.onmessage = (e) => {
        const ev = JSON.parse(e.data);
        if (ev.type === "notification") {
          qc.invalidateQueries({ queryKey: ["notifications"] });
          toast(ev.notification.title, { description: ev.notification.body });
        }
        if (ev.type === "task.update") {
          qc.invalidateQueries({ queryKey: ["tasks"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
        }
        subs.current.forEach((fn) => fn(ev));
      };
      ws.onclose = () => {
        clearInterval(ping);
        if (!stopped) {
          tries += 1;
          timer = setTimeout(connect, Math.min(15000, 1000 * 2 ** tries));
        }
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(ping);
      if (ws) ws.close();
    };
  }, [qc]);

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  return <Ctx.Provider value={{ subscribe }}>{children}</Ctx.Provider>;
}

export const useRealtime = () => useContext(Ctx);
